import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const INITIAL_PEDIATRIC_BRAIN_SPINE_DATA = {
  examType: 'Pediatric Brain and Spine',
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: '',
  comments: '',
  brain: {
    evaluated: false,
    parenchyma: '',
    hemorrhage: '',
    ventricles: ''
  },
  spine: {
    evaluated: false,
    conus: '',
    spinalCanal: '',
    softTissues: ''
  }
};

function PediatricBrainSpineForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Brain, 1=Spine
  const [activeTab, setActiveTab] = useState(0);

  // Fetch dynamic technologists list
    const { technologists, defaultTech } = useTechnologists();
  

  const { formData, updateField, updateNestedField, handleSubmit: hookHandleSubmit,
    handlePreview, resetForm, isLoading, error, success, clearMessages, fieldErrors, draftBannerProps } = useUltrasoundForm(
    INITIAL_PEDIATRIC_BRAIN_SPINE_DATA,
    'Pediatric Brain and Spine',
    { token, facilityInfo }
  );

  useEffect(() => {
   if (defaultTech && !formData.technologist) {
     updateField('technologist', defaultTech);
   }
  }, [defaultTech, formData.technologist]);

  

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await hookHandleSubmit(e);
  };

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={handleFormSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history"
            />
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Brain
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Spine
            </button>
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {/* Brain Tab */}
            {activeTab === 0 && (
              <div className="tab-content-grid">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.brain.evaluated}
                      onChange={(e) => updateNestedField('brain', 'evaluated', e.target.checked)}
                    />
                    {' '}Brain Evaluated
                  </label>
                </div>

                <div style={{ opacity: formData.brain.evaluated ? 1 : 0.4, pointerEvents: formData.brain.evaluated ? 'auto' : 'none' }}>
                  <h3 style={{ color: 'var(--color-primary)' }}>Brain</h3>
            
            <div className="form-group">
              <label>Parenchyma:</label>
              <select
                value={formData.brain.parenchyma}
                onChange={(e) => updateNestedField('brain', 'parenchyma', e.target.value)}
                disabled={!formData.brain.evaluated}
              >
                <option value="">Select</option>
                <option value="Normal">Normal</option>
                <option value="Abnormal">Abnormal</option>
              </select>
            </div>

            <div className="form-group">
              <label>Hemorrhage:</label>
              <select
                value={formData.brain.hemorrhage}
                onChange={(e) => updateNestedField('brain', 'hemorrhage', e.target.value)}
                disabled={!formData.brain.evaluated}
              >
                <option value="">Select</option>
                <option value="None">None</option>
                <option value="Intraparenchymal hemorrhage">Intraparenchymal hemorrhage</option>
                <option value="Intraventricular hemorrhage">Intraventricular hemorrhage</option>
                <option value="Both intraventricular and intraparenchymal hemorrhage">Both intraventricular and intraparenchymal hemorrhage</option>
              </select>
            </div>

            <div className="form-group">
              <label>Ventricles:</label>
              <select
                value={formData.brain.ventricles}
                onChange={(e) => updateNestedField('brain', 'ventricles', e.target.value)}
                disabled={!formData.brain.evaluated}
              >
                <option value="">Select</option>
                <option value="Normal">Normal</option>
                <option value="Enlarged">Enlarged</option>
                <option value="Small">Small</option>
              </select>
            </div>
                </div>
              </div>
            )}

            {/* Spine Tab */}
            {activeTab === 1 && (
              <div className="tab-content-grid">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.spine.evaluated}
                      onChange={(e) => updateNestedField('spine', 'evaluated', e.target.checked)}
                    />
                    {' '}Spine Evaluated
                  </label>
                </div>

                <div style={{ opacity: formData.spine.evaluated ? 1 : 0.4, pointerEvents: formData.spine.evaluated ? 'auto' : 'none' }}>
                  <h3 style={{ color: 'var(--color-primary)' }}>Spine</h3>
            
            <div className="form-group">
              <label>Conus:</label>
              <select
                value={formData.spine.conus}
                onChange={(e) => updateNestedField('spine', 'conus', e.target.value)}
                disabled={!formData.spine.evaluated}
              >
                <option value="">Select</option>
                <option value="Normal (L1-L2)">Normal (L1-L2)</option>
                <option value="Abnormal">Abnormal</option>
              </select>
            </div>

            <div className="form-group">
              <label>Spinal Canal:</label>
              <select
                value={formData.spine.spinalCanal}
                onChange={(e) => updateNestedField('spine', 'spinalCanal', e.target.value)}
                disabled={!formData.spine.evaluated}
              >
                <option value="">Select</option>
                <option value="Normal">Normal</option>
                <option value="Abnormal">Abnormal</option>
              </select>
            </div>

            <div className="form-group">
              <label>Soft Tissues:</label>
              <select
                value={formData.spine.softTissues}
                onChange={(e) => updateNestedField('spine', 'softTissues', e.target.value)}
                disabled={!formData.spine.evaluated}
              >
                <option value="">Select</option>
                <option value="Normal">Normal</option>
                <option value="Sinus tract">Sinus tract</option>
                <option value="Mass">Mass</option>
              </select>
            </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button 
            type="submit" 
            disabled={isLoading} 
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button 
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default PediatricBrainSpineForm;
